/**
 * IL TAVOLO DEGLI SCACCHI — le regole pure (E6 di ADR-0193, P4a). Le esegue anche il gate sotto Node: niente `@/`, niente
 * React, niente `import.meta.env`; i tipi del pacchetto arrivano con `import type` (cancellati prima di girare).
 *
 * Cosa sta qui e perche':
 *  · il TICKET: la forma del corpo e della risposta del BFF (`POST /v1/games/chess/session-ticket`, ADR-0193 §3.5). Monouso e
 *    di 30 s: chi lo chiede lo chiede a OGNI connessione (`hooks/useChessTavolo.ts`), mai da una cache;
 *  · gli ERRORI come frasi: ogni codice che il client o il game host puo' mandare ha la SUA frase, e la tabella e' tipata
 *    sull'unione dei codici — un codice nuovo nel pacchetto non compila finche' non ha una riga. «Mai un errore generico»
 *    (dispatch tma-orcr-37); un codice che a runtime non conosciamo ha la frase `unknown` col codice nudo, non un «errore»;
 *  · l'ESITO: i codici di `reason` che il pacchetto elenca (CONTRATTO §6) e la chiave i18n di ciascuno;
 *  · il QUANDO si chiede conferma per uscire dal tavolo (L-2927 (b): all'assenza l'orologio di chi ha la mossa CORRE).
 */
import type { ClientErrorCode, GameErrorCode, MatchStatePayload, OutcomeKind, Player } from '@qlash-it/qchess-board-ui';

// ─────────────────────────────────────────────────────────────────────────────
// 1. Il ticket
// ─────────────────────────────────────────────────────────────────────────────

/** La rotta del BFF Limulo (mirror di `chess.session.ticket.issue`, CONTRATTO §2 punto 4). Una POST: non si ritenta mai. */
export const CHESS_TICKET_ROUTE = '/v1/games/chess/session-ticket';

/** L'`id` della rotta `chess/match/:id` e' l'uuid della partita del game host: altro non apre nessun socket. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const chessMatchIdValido = (id: string | null | undefined): id is string => typeof id === 'string' && UUID.test(id);

/** Lo scopo del ticket di chi GIOCA. ⛔ Solo `match:`: `queue`/`invite` non si portano (ADR-0193 §3.5, A193:103). */
export const chessTicketPurpose = (matchId: string): string => `match:${matchId}`;

export const chessTicketBody = (matchId: string): { purpose: string } => ({ purpose: chessTicketPurpose(matchId) });

export interface ChessTicketGrant {
  ticket: string;
  wsUrl: string;
}

const asRecord = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const testo = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);

/**
 * Il ticket dal `data` del BFF, `{ ticket, expiresInSeconds, wsUrl, purpose }`. Un ticket senza indirizzo, o un indirizzo senza
 * ticket, e' `null` (= `TICKET_UNAVAILABLE` nel client): `wsUrl: ""` e' proprio il caso di `PUBLIC_WS_URL` mancante sul game
 * host. `url` e' accettato come sinonimo di `wsUrl`, come fa il pacchetto.
 */
export function chessTicketGrantOf(data: unknown): ChessTicketGrant | null {
  const r = asRecord(data);
  const ticket = testo(r.ticket);
  const wsUrl = testo(r.wsUrl) ?? testo(r.url);
  return ticket !== null && wsUrl !== null ? { ticket, wsUrl } : null;
}

/**
 * Un 200 che NON porta un ticket completo, col perche': quale dei due campi manca (il ticket vince, se mancano entrambi).
 * Serve alla riga di diagnosi (I-545, `lib/chess/diagnosi.ts`): `null` diceva «non c'e'» senza dire cosa. NON porta mai il
 * valore di nessuno dei due campi, solo la loro assenza.
 */
export interface ChessTicketAssente {
  assente: 'ticket' | 'wsUrl';
}

/** Come `chessTicketGrantOf`, ma l'assenza dice cosa manca invece di essere `null`. Stessa regola di lettura, in un posto solo. */
export function chessTicketRispostaOf(data: unknown): ChessTicketGrant | ChessTicketAssente {
  const grant = chessTicketGrantOf(data);
  if (grant !== null) return grant;
  return { assente: testo(asRecord(data).ticket) === null ? 'ticket' : 'wsUrl' };
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. Gli errori, come frasi
// ─────────────────────────────────────────────────────────────────────────────

export type ChessErrorCode = ClientErrorCode | GameErrorCode;

/**
 * La chiave (sotto `errors.` nel namespace `chessMatch`) di OGNI codice. Tipata sull'unione: aggiungere un codice al pacchetto
 * senza una riga qui non compila. Una chiave per codice, anche dove due frasi coincidono oggi: sono due cose che possono
 * divergere, e la prima volta che divergono non deve servire cambiare il codice.
 */
export const CHIAVE_ERRORE: Record<ChessErrorCode, string> = {
  // del client — prima del game host
  TICKET_UNAVAILABLE: 'ticketUnavailable',
  UNAUTHORIZED: 'unauthorized',
  INVALID_TICKET: 'invalidTicket',
  TOO_MANY_CONNECTIONS: 'tooManyConnections',
  NETWORK: 'network',
  JOIN_REFUSED: 'joinRefused',
  TIMEOUT: 'timeout',
  NOT_CONNECTED: 'notConnected',
  // del game host — tentativi falliti, mai verdetti
  NOT_FOUND: 'notFound',
  VALIDATION: 'validation',
  NOT_STARTED: 'notStarted',
  GAME_OVER: 'gameOver',
  PURPOSE_MISMATCH: 'purposeMismatch',
  THROTTLED: 'throttled',
  AT_CAPACITY: 'atCapacity',
  OUT_OF_SEQUENCE: 'outOfSequence',
  ALREADY_APPLIED: 'alreadyApplied',
  ILLEGAL_MOVE: 'illegalMove',
  NOT_YOUR_TURN: 'notYourTurn',
  NO_CLAIM: 'noClaim',
  BETWEEN_GAMES: 'betweenGames',
  NO_DRAW_OFFER: 'noDrawOffer',
  DRAW_OFFER_LIMIT: 'drawOfferLimit',
  UNAVAILABLE: 'unavailable',
  INTERNAL: 'internal',
};

/** La chiave di un codice qualunque, anche uno che questa build non conosce: `unknown` (la frase porta il codice nudo). */
export function chiaveErrore(code: string): string {
  return Object.prototype.hasOwnProperty.call(CHIAVE_ERRORE, code) ? CHIAVE_ERRORE[code as ChessErrorCode] : 'unknown';
}

/**
 * Gli errori della CONNESSIONE (il client, prima del game host): la banda del tavolo li dice, e non fanno un avviso sulla
 * scacchiera. Gli altri sono tentativi d'azione falliti (una mossa, una patta) e si mostrano su di essa per qualche secondo.
 * `TIMEOUT` sta fra gli ultimi: un'azione di cui non si sa l'esito, e la posizione vera arriva col prossimo stato.
 */
const DI_CONNESSIONE: ReadonlySet<string> = new Set<ClientErrorCode>([
  'TICKET_UNAVAILABLE', 'UNAUTHORIZED', 'INVALID_TICKET', 'TOO_MANY_CONNECTIONS', 'NETWORK', 'JOIN_REFUSED', 'NOT_CONNECTED',
]);
export const eErroreDiConnessione = (code: string): boolean => DI_CONNESSIONE.has(code);

// ─────────────────────────────────────────────────────────────────────────────
// 3. Cadenza e note del colore
// ─────────────────────────────────────────────────────────────────────────────

export type ChessCadenza =
  | { kind: 'orologio'; nome: 'Blitz' | 'Rapid'; base: number; incremento: number; decisiva: boolean }
  | { kind: 'senza'; decisiva: boolean }
  | { kind: 'sconosciuta'; decisiva: boolean };

const NOMI: Record<string, 'Blitz' | 'Rapid'> = { blitz: 'Blitz', rapid: 'Rapid' };

/** `chess-blitz-3-2`, `chess-rapid-10-5`, `chess-unlimited`, ciascuna anche `-decisive` (R5, R4). Altro = `sconosciuta`. */
export function cadenzaOf(mode: string): ChessCadenza {
  const decisiva = mode.endsWith('-decisive');
  const m = (decisiva ? mode.slice(0, -'-decisive'.length) : mode).match(/^chess-(?:(blitz|rapid)-(\d+)-(\d+)|(unlimited))$/);
  if (m === null) return { kind: 'sconosciuta', decisiva };
  if (m[4] === 'unlimited') return { kind: 'senza', decisiva };
  return { kind: 'orologio', nome: NOMI[m[1]], base: Number(m[2]), incremento: Number(m[3]), decisiva };
}

/** Il colore nell'altra partita: a ogni partita dell'incontro i colori si invertono (RG, «Armageddon» compreso). */
export const colorePoi = (c: Player): Player => (c === 'W' ? 'B' : 'W');

/** La nota sotto il nome: il colore, e nell'Armageddon il vantaggio di chi lo ha (R4: al bianco +60 s, al nero basta la patta). */
export function notaDelColore(colore: Player, armageddon: boolean): { colore: Player; armageddon: 'piuTempo' | 'bastaLaPatta' | null } {
  if (!armageddon) return { colore, armageddon: null };
  return { colore, armageddon: colore === 'W' ? 'piuTempo' : 'bastaLaPatta' };
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. L'esito
// ─────────────────────────────────────────────────────────────────────────────

/**
 * I `reason` di CONTRATTO §6, tutti: vittorie, patte, nessun risultato. `OutcomeLabels.reason` e' indicizzato per codice, e un
 * codice senza frase rende NIENTE (nessuna parola nel pacchetto): per questo l'elenco e' qui, e il gate confronta le chiavi
 * i18n con questo elenco.
 */
export const MOTIVI_ESITO = [
  'checkmate', 'resign', 'timeout', 'graceForfeit', 'noShow', 'noShowLot', 'noFirstMove', 'armageddonDraw',
  'agreement', 'stalemate', 'insufficient_material', 'fivefold_repetition', 'seventy_five_moves',
  'threefold_repetition', 'fifty_moves', 'timeout_vs_insufficient_material',
  'unattended',
] as const;

export type ChessMotivoEsito = (typeof MOTIVI_ESITO)[number];

/** Le chiavi dei titoli dell'esito, per `OutcomeKind` (il `win_for` ha la sua frase col vincitore, per lo spettatore). */
export const TITOLO_ESITO: Record<OutcomeKind, string> = {
  win: 'win',
  loss: 'loss',
  win_for: 'winFor',
  draw: 'draw',
  no_result: 'noResult',
};

// ─────────────────────────────────────────────────────────────────────────────
// 5. Lasciare il tavolo
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Uscire dal tavolo costa tempo? Si': la partita e' viva, c'e' un orologio che corre e corre il MIO (L-2927 (b): il tempo di
 * chi ha la mossa scorre anche se e' assente, e la grazia e' solo il tetto). Allora il tasto indietro chiede prima («Lasciare
 * il tavolo?»); in ogni altro caso — non e' il mio turno, partita senza orologio, partita finita, spettatore — si esce subito.
 * Lo stesso predicato del backgammon (`lib/qgammon/lasciareIlTavolo.ts`), scritto sullo stato degli scacchi.
 */
export function uscitaCostaOrologio(state: MatchStatePayload | null, mio: Player | null): boolean {
  if (state === null || mio === null) return false;
  if (state.status !== 'live') return false;
  const c = state.clocks;
  return c.running && !c.unlimited && c.owner === mio;
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. Fra una partita e l'altra della «decisiva» (R4)
// ─────────────────────────────────────────────────────────────────────────────

/** Le partite di una decisiva: la prima, la ripetizione a colori invertiti, l'Armageddon. */
export const PARTITE_DELLA_DECISIVA = 3;

/**
 * Cosa dire fra la partita `numero` (patta) e la prossima. Un incontro «decisivo» continua SOLO dopo una patta: dopo la 1 si
 * gioca la ripetizione a colori invertiti (e l'avviso dice che, se e' ancora patta, c'e' l'Armageddon), dopo la 2 si gioca
 * l'Armageddon (R4). Dopo la 3 non c'e' un'altra: l'incontro e' finito. I colori si invertono a ogni partita.
 */
export function traLePartite(numero: number): { prossima: number; armageddon: boolean; avvisoArmageddon: boolean } | null {
  if (!Number.isInteger(numero) || numero < 1 || numero >= PARTITE_DELLA_DECISIVA) return null;
  return { prossima: numero + 1, armageddon: numero + 1 === PARTITE_DELLA_DECISIVA, avvisoArmageddon: numero === 1 };
}

/** Il percorso del tavolo di QUESTA partita: il freno dell'indietro e la barra lo confrontano col percorso di oggi. */
export const chessMatchPath = (matchId: string): string => `/chess/match/${matchId}`;
export const eIlTavoloScacchi = (pathname: string, matchId: string): boolean => pathname === chessMatchPath(matchId);

/**
 * L'ultima mossa come la scrive la riga delle informazioni: «6… Bb4+» (nera) o «7. Bd2» (bianca), il SAN COME LO MANDA IL SERVER
 * e il numero dalla semimossa (`ply` 1 = prima del Bianco). Senza mossa: `null`.
 */
export function mossaSan(lastMove: { ply: number; san: string } | null): string | null {
  if (lastMove === null || !Number.isInteger(lastMove.ply) || lastMove.ply < 1 || lastMove.san === '') return null;
  const numero = Math.ceil(lastMove.ply / 2);
  return lastMove.ply % 2 === 1 ? `${numero}. ${lastMove.san}` : `${numero}… ${lastMove.san}`;
}

/** Il colore che avevo nella partita `numero` dell'incontro, dal `seat` (= il colore nella 1): si inverte a ogni partita (RG). */
export function coloreAllaPartita(seat: Player, numero: number): Player {
  return numero % 2 === 1 ? seat : colorePoi(seat);
}
